import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock, User, Share2, Copy, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { blogPosts } from "./BlogPage";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.id === slug);

  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  // Close share menu on outside click
  useEffect(() => {
    if (!shareMenuOpen) return;
    const handler = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShareMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [shareMenuOpen]);

  const handleShareClick = async () => {
    if (!post) return;
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    // Prefer native share sheet (mobile → WhatsApp / IG / Messages / etc.)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled — fall through only if not an abort
        if (err && err.name === "AbortError") return;
      }
    }
    // Desktop fallback: toggle share menu
    setShareMenuOpen((s) => !s);
  };

  const handleCopyLink = async () => {
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback: create a temp textarea
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Inject Article JSON-LD into <head> when a real post is loaded.
  // Direct DOM injection works in both runtime and react-snap prerender (2s waitFor).
  useEffect(() => {
    if (!post) return;
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Organization",
        name: post.author || "Prithira Team",
        url: "https://www.prithiraglobal.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Prithira Global",
        logo: {
          "@type": "ImageObject",
          url: "https://www.prithiraglobal.com/logo.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://www.prithiraglobal.com/blog/${post.id}`,
      },
      articleSection: post.category,
    };
    const id = "blog-article-schema";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(articleSchema);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <div className="pt-32 pb-20 min-h-screen">
        <div className="container-custom text-center">
          <h1 className="heading-lg text-stone-900 mb-4">Post Not Found</h1>
          <p className="text-stone-600 mb-8">The blog post you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/blog">
            <Button className="rounded-full px-8 py-6 bg-primary text-primary-foreground">
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="noise-overlay">
      <Helmet
        title={`${post.title} | Prithira Global Blog`}
        meta={[
          { name: "description", content: post.excerpt },
          { property: "og:type", content: "article" },
          { property: "og:title", content: post.title },
          { property: "og:description", content: post.excerpt },
          {
            property: "og:image",
            content: post.image.startsWith("http")
              ? post.image
              : `https://www.prithiraglobal.com${post.image}`,
          },
          {
            property: "og:url",
            content: `https://www.prithiraglobal.com/blog/${post.id}`,
          },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: post.title },
          { name: "twitter:description", content: post.excerpt },
          {
            name: "twitter:image",
            content: post.image.startsWith("http")
              ? post.image
              : `https://www.prithiraglobal.com${post.image}`,
          },
        ]}
        link={[
          {
            rel: "canonical",
            href: `https://www.prithiraglobal.com/blog/${post.id}`,
          },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-primary mb-6 hover:underline"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
            <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium inline-block mb-4">
              {post.category}
            </span>
            <h1 className="heading-lg text-stone-900 mb-6">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-stone-600 text-sm">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-12 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg overflow-hidden bg-stone-100">
              <img
                src={post.image}
                alt={post.title}
                className="block w-full h-auto max-w-full object-cover"
                style={{ borderRadius: "8px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose prose-lg prose-stone max-w-none
                prose-headings:font-heading prose-headings:text-stone-900
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-stone-600 prose-p:font-light prose-p:leading-relaxed
                prose-li:text-stone-600 prose-li:font-light
                prose-strong:text-stone-900 prose-strong:font-medium
                prose-ul:my-4 prose-li:my-1"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div
                data-testid="blog-post-tags"
                className="mt-10 pt-8 border-t border-stone-200"
              >
                <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog?tag=${encodeURIComponent(tag)}`}
                      data-testid={`blog-tag-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                      className="inline-flex items-center text-xs font-medium py-1.5 px-3 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Share & CTA */}
            <div className="mt-12 pt-8 border-t border-stone-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4" ref={shareRef}>
                  <span className="text-stone-600 text-sm">Share this article:</span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleShareClick}
                      data-testid="blog-share-btn"
                      aria-label="Share this article"
                      aria-expanded={shareMenuOpen}
                      className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>

                    {shareMenuOpen && (
                      <div
                        data-testid="blog-share-menu"
                        className="absolute left-0 sm:left-auto sm:right-0 top-12 z-30 w-64 rounded-2xl border border-stone-200 bg-white shadow-xl p-2"
                      >
                        <a
                          href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + " — " + window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="share-whatsapp"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-stone-700 text-sm"
                          onClick={() => setShareMenuOpen(false)}
                        >
                          <span className="w-8 h-8 rounded-full bg-[#25D366]/10 text-[#25D366] flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.992c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413Z"/></svg>
                          </span>
                          WhatsApp
                        </a>
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="share-twitter"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-stone-700 text-sm"
                          onClick={() => setShareMenuOpen(false)}
                        >
                          <span className="w-8 h-8 rounded-full bg-stone-900/10 text-stone-900 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          </span>
                          X (Twitter)
                        </a>
                        <a
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="share-linkedin"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-stone-700 text-sm"
                          onClick={() => setShareMenuOpen(false)}
                        >
                          <span className="w-8 h-8 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                          </span>
                          LinkedIn
                        </a>
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="share-facebook"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-stone-700 text-sm"
                          onClick={() => setShareMenuOpen(false)}
                        >
                          <span className="w-8 h-8 rounded-full bg-[#1877F2]/10 text-[#1877F2] flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                          </span>
                          Facebook
                        </a>
                        <button
                          type="button"
                          onClick={handleCopyLink}
                          data-testid="share-copy-link"
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-stone-700 text-sm text-left"
                        >
                          <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </span>
                          {copied ? "Link copied!" : "Copy link"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <Link to="/partner">
                  <Button className="rounded-full px-6 py-5 bg-primary text-primary-foreground hover:bg-primary/90">
                    Partner With Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="section-spacing bg-sand-200/30">
        <div className="container-custom">
          <h2 className="heading-md text-stone-900 mb-8 text-center">
            More <span className="italic">Articles</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {blogPosts
              .filter((p) => p.id !== post.id)
              .slice(0, 2)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="group bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-lg text-stone-900 group-hover:text-primary transition-colors">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
